import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  articleInfo?: {
    publishDate?: string;
    modifiedDate?: string;
    author?: string;
    tags?: string[];
  };
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  schema,
  articleInfo,
}: SEOHeadProps) {
  useEffect(() => {
    const fullTitle = title.includes("51每日大赛") ? title : `${title} | 51每日大赛`;
    if (typeof document === 'undefined') {
      return null;
    }

    document.title = fullTitle;

    const setMeta = (name: string, content: string, prop?: string) => {
      let el: HTMLMetaElement | null;
      if (prop) {
        el = document.querySelector(`meta[property="${prop}"]`);
      } else {
        el = document.querySelector(`meta[name="${name}"]`);
      }
      if (el) {
        el.setAttribute("content", content);
      } else {
        const meta = document.createElement("meta");
        if (prop) {
          meta.setAttribute("property", prop);
        } else {
          meta.setAttribute("name", name);
        }
        meta.setAttribute("content", content);
        document.head.appendChild(meta);
      }
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta("og:title", fullTitle, "og:title");
    setMeta("og:description", description, "og:description");
    setMeta("og:type", ogType, "og:type");
    if (canonicalUrl) {
      setMeta("og:url", canonicalUrl, "og:url");
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (link) {
        link.setAttribute("href", canonicalUrl);
      } else {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        link.setAttribute("href", canonicalUrl);
        document.head.appendChild(link);
      }
    }
    if (ogImage) setMeta("og:image", ogImage, "og:image");

    setMeta("twitter:card", "summary_large_image", undefined);
    setMeta("twitter:title", fullTitle, undefined);
    setMeta("twitter:description", description, undefined);

    if (articleInfo?.publishDate) {
      let el = document.querySelector('meta[name="article:published_time"]');
      if (el) el.setAttribute("content", articleInfo.publishDate);
    }
    if (articleInfo?.modifiedDate) {
      let el = document.querySelector('meta[name="article:modified_time"]');
      if (el) el.setAttribute("content", articleInfo.modifiedDate);
    }
    if (articleInfo?.author) {
      let el = document.querySelector('meta[name="author"]');
      if (el) el.setAttribute("content", articleInfo.author);
    }

    document.documentElement.lang = "zh-CN";

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]');
    existingScripts.forEach((s) => s.remove());

    if (schema) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "true");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schema, articleInfo]);

  return null;
}